import { promises as fs } from "node:fs"
import path from "node:path"

import type { Problem } from "@/types/problem"

const problemsContentDir = path.join(process.cwd(), "content", "problems")

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

function isString(value: unknown): value is string {
  return typeof value === "string"
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === "string")
}

function ensureProblem(value: unknown, filePath: string): Problem {
  if (!isObject(value)) {
    throw new Error(`Invalid problem JSON in ${filePath}: root must be an object`)
  }

  const {
    id,
    slug,
    title,
    excerpt,
    description,
    category,
    severity,
    symptom,
    quickAnswer,
    heroEyebrow,
    publishedAt,
    updatedAt,
    checks,
    causes,
    fixSteps,
    preventionTips,
    warning,
    relatedRecipes,
    relatedGuides,
    relatedCalculators,
    faq,
    tags,
    featured,
  } = value

  if (
    !isString(id) ||
    !isString(slug) ||
    !isString(title) ||
    !isString(excerpt) ||
    !isString(description) ||
    !isString(category) ||
    !isString(severity) ||
    !isString(symptom) ||
    !isString(quickAnswer) ||
    !isString(heroEyebrow) ||
    !isString(publishedAt)
  ) {
    throw new Error(`Invalid required fields in ${filePath}`)
  }

  if (!Array.isArray(causes) || !Array.isArray(fixSteps) || !isStringArray(preventionTips)) {
    throw new Error(`Invalid structured fields in ${filePath}`)
  }

  return {
    id,
    slug,
    title,
    excerpt,
    description,
    category: category as Problem["category"],
    severity: severity as Problem["severity"],
    symptom,
    quickAnswer,
    heroEyebrow,
    publishedAt,
    updatedAt: isString(updatedAt) ? updatedAt : undefined,
    checks: Array.isArray(checks)
      ? checks
          .filter(isObject)
          .map((item) => ({
            label: isString(item.label) ? item.label : "",
            value: isString(item.value) ? item.value : "",
          }))
          .filter((item) => item.label && item.value)
      : undefined,
    causes: causes
      .filter(isObject)
      .map((item) => ({
        title: isString(item.title) ? item.title : "",
        description: isString(item.description) ? item.description : "",
      }))
      .filter((item) => item.title && item.description),
    fixSteps: fixSteps
      .filter(isObject)
      .map((item) => ({
        title: isString(item.title) ? item.title : "",
        description: isString(item.description) ? item.description : "",
        important: typeof item.important === "boolean" ? item.important : undefined,
      }))
      .filter((item) => item.title && item.description),
    preventionTips,
    warning:
      isObject(warning) && isString(warning.title) && isString(warning.text)
        ? {
            title: warning.title,
            text: warning.text,
          }
        : undefined,
    relatedRecipes: Array.isArray(relatedRecipes)
      ? relatedRecipes
          .filter(isObject)
          .map((item) => ({
            title: isString(item.title) ? item.title : "",
            href: isString(item.href) ? item.href : "",
            description: isString(item.description) ? item.description : undefined,
          }))
          .filter((item) => item.title && item.href)
      : undefined,
    relatedGuides: Array.isArray(relatedGuides)
      ? relatedGuides
          .filter(isObject)
          .map((item) => ({
            title: isString(item.title) ? item.title : "",
            href: isString(item.href) ? item.href : "",
            description: isString(item.description) ? item.description : undefined,
          }))
          .filter((item) => item.title && item.href)
      : undefined,
    relatedCalculators: Array.isArray(relatedCalculators)
      ? relatedCalculators
          .filter(isObject)
          .map((item) => ({
            title: isString(item.title) ? item.title : "",
            href: isString(item.href) ? item.href : "",
            description: isString(item.description) ? item.description : undefined,
          }))
          .filter((item) => item.title && item.href)
      : undefined,
    faq: Array.isArray(faq)
      ? faq
          .filter(isObject)
          .map((item) => ({
            question: isString(item.question) ? item.question : "",
            answer: isString(item.answer) ? item.answer : "",
          }))
          .filter((item) => item.question && item.answer)
      : undefined,
    tags: isStringArray(tags) ? tags : undefined,
    featured: typeof featured === "boolean" ? featured : undefined,
  }
}

async function readProblemFile(filePath: string) {
  const raw = await fs.readFile(filePath, "utf-8")
  const normalized = raw.replace(/^\uFEFF/, "").trim()

  if (!normalized) {
    throw new Error(`Problem JSON file is empty: ${filePath}`)
  }

  try {
    const parsed = JSON.parse(normalized) as unknown
    return ensureProblem(parsed, filePath)
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unknown JSON parse error"
    throw new Error(`Failed to parse JSON in ${filePath}: ${message}`)
  }
}

async function collectJsonFilesRecursively(dirPath: string): Promise<string[]> {
  const entries = await fs.readdir(dirPath, { withFileTypes: true })
  const nested = await Promise.all(
    entries.map(async (entry) => {
      const fullPath = path.join(dirPath, entry.name)

      if (entry.isDirectory()) {
        return collectJsonFilesRecursively(fullPath)
      }

      if (entry.isFile() && entry.name.toLowerCase().endsWith(".json")) {
        return [fullPath]
      }

      return []
    }),
  )

  return nested.flat()
}

function parseDate(value: string | undefined) {
  if (!value) {
    return 0
  }

  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? 0 : parsed
}

export async function getAllProblemsContent() {
  const dirExists = await fs
    .access(problemsContentDir)
    .then(() => true)
    .catch(() => false)

  if (!dirExists) {
    return [] as Problem[]
  }

  const filePaths = await collectJsonFilesRecursively(problemsContentDir)

  const items: Problem[] = []

  for (const filePath of filePaths) {
    const item = await readProblemFile(filePath)
    items.push(item)
  }

  return items.sort((a, b) => {
    if (Boolean(b.featured) !== Boolean(a.featured)) {
      return Number(Boolean(b.featured)) - Number(Boolean(a.featured))
    }

    const leftUpdated = parseDate(a.updatedAt ?? a.publishedAt)
    const rightUpdated = parseDate(b.updatedAt ?? b.publishedAt)

    if (rightUpdated !== leftUpdated) {
      return rightUpdated - leftUpdated
    }

    return a.title.localeCompare(b.title, "uk")
  })
}

export async function getProblemContentBySlug(slug: string) {
  const dirExists = await fs
    .access(problemsContentDir)
    .then(() => true)
    .catch(() => false)

  if (!dirExists) {
    return null
  }

  const filePaths = await collectJsonFilesRecursively(problemsContentDir)
  const targetPath = filePaths.find((filePath) => path.basename(filePath, ".json") === slug)

  if (!targetPath) {
    return null
  }

  return readProblemFile(targetPath)
}